import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import {
  Plus, Send, Zap, Brain, Trash2, MessageSquare,
  ChevronLeft, ChevronRight, Sparkles, User,
} from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { aiService } from "@/hooks/api/aiService"
import { useStudentSummary, buildStudentContextPrompt } from "@/hooks/useStudentSummary"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

// ─── Session helpers ──────────────────────────────────────────────────────────
function loadSessions() {
  const raw = StorageService.read("aiChats")
  return Array.isArray(raw) ? raw : []
}
function saveSessions(s) { StorageService.write("aiChats", s) }
function makeId() { return `${Date.now()}_${Math.random().toString(36).slice(2,6)}` }
function newSession(welcomeMsg) {
  return {
    id: makeId(),
    title: "New conversation",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    messages: [{ id: "0", role: "assistant", content: welcomeMsg }],
  }
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function Bubble({ msg }) {
  const isAI = msg.role === "assistant"
  return (
    <div className={`flex items-end gap-2.5 ${isAI ? "" : "flex-row-reverse"}`}>
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mb-0.5 ${
        isAI ? "bg-indigo-100" : "bg-slate-200"
      }`}>
        {isAI
          ? <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
          : <User className="w-3.5 h-3.5 text-slate-600" />
        }
      </div>
      {/* Bubble */}
      <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
        isAI
          ? "bg-white border border-slate-100 shadow-sm text-slate-800 rounded-bl-sm"
          : "bg-indigo-600 text-white rounded-br-sm"
      }`}>
        {msg.content}
      </div>
    </div>
  )
}

// ─── Typing indicator ─────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="flex items-end gap-2.5">
      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
      </div>
      <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
        {[0, 150, 300].map((d) => (
          <div key={d} className="w-1.5 h-1.5 bg-indigo-300 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  )
}

// ─── History sidebar ──────────────────────────────────────────────────────────
function HistoryPanel({ sessions, activeId, onSelect, onDelete, onNew, collapsed, onToggle }) {
  return (
    <aside className={`flex flex-col border-r border-slate-100 bg-white transition-all duration-300 shrink-0 ${
      collapsed ? "w-14" : "w-64"
    }`}>
      {/* Header */}
      <div className={`flex items-center border-b border-slate-100 h-14 px-3 ${collapsed ? "justify-center" : "justify-between"}`}>
        {!collapsed && <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Chats</p>}
        <button onClick={onToggle} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors">
          {collapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronLeft className="w-4 h-4 text-slate-500" />}
        </button>
      </div>

      {/* New chat button */}
      <div className={`p-2 border-b border-slate-100 ${collapsed ? "flex justify-center" : ""}`}>
        <button
          onClick={onNew}
          className={`flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all active:scale-95 ${
            collapsed ? "w-9 h-9 justify-center" : "w-full px-3 py-2"
          }`}
        >
          <Plus className="w-3.5 h-3.5 shrink-0" />
          {!collapsed && "New chat"}
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
        {sessions.map((s) => {
          const isActive = s.id === activeId
          return (
            <div key={s.id} className="group relative">
              <button
                onClick={() => onSelect(s.id)}
                className={`w-full text-left rounded-xl transition-all ${
                  collapsed ? "flex justify-center p-2" : "flex items-center gap-2 px-3 py-2.5"
                } ${isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700 hover:bg-slate-50"}`}
                title={collapsed ? s.title : ""}
              >
                <MessageSquare className={`w-4 h-4 shrink-0 ${isActive ? "text-indigo-500" : "text-slate-400"}`} />
                {!collapsed && (
                  <span className="flex-1 text-xs font-medium truncate">{s.title}</span>
                )}
              </button>
              {!collapsed && (
                <button
                  onClick={() => onDelete(s.id)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-lg bg-white border border-slate-200 hover:bg-red-50 hover:border-red-200 items-center justify-center opacity-0 group-hover:opacity-100 transition-all hidden group-hover:flex"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
            </div>
          )
        })}
        {sessions.length === 0 && !collapsed && (
          <p className="text-xs text-slate-400 text-center py-6">No conversations yet</p>
        )}
      </div>
    </aside>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AICompanionPage() {
  const summary = useStudentSummary()
  const systemPrompt = summary
    ? buildStudentContextPrompt(summary)
    : "You are a helpful AI study companion. Be warm, encouraging, and concise."

  const studentName = summary?.name ?? "there"
  const aiModel = StorageService.read("students")?.find(
    (s) => s.userId === AuthStore.getState().currentUser?._id ||
           s.email  === AuthStore.getState().currentUser?.email
  )?.preferences?.aiModel ?? "haiku"

  const welcomeMsg = `Hi ${studentName}! I'm your EarlyScape AI Companion. Ask me anything about your studies, career paths, goals, or any subject you're working on.`

  const [sessions, setSessions] = useState(() => {
    const saved = loadSessions()
    if (saved.length) return saved
    const first = newSession(welcomeMsg)
    saveSessions([first])
    return [first]
  })
  const [activeId, setActiveId] = useState(() => sessions[0]?.id)
  const [historyCollapsed, setHistoryCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [input, setInput] = useState("")
  const messagesEndRef = useRef(null)
  const historyRef = useRef([])
  const inputRef = useRef(null)

  const active = sessions.find((s) => s.id === activeId) ?? sessions[0]
  const messages = active?.messages ?? []

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }) }, [messages, isLoading])

  useEffect(() => {
    if (!active) return
    historyRef.current = active.messages.map((m) => ({ role: m.role, content: m.content }))
  }, [activeId])

  const updateSession = useCallback((id, updater) => {
    setSessions((prev) => {
      const updated = prev.map((s) => s.id === id ? { ...updater(s), updatedAt: new Date().toISOString() } : s)
      saveSessions(updated)
      return updated
    })
  }, [])

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return

    const userMsg = { id: makeId(), role: "user", content: text }
    updateSession(activeId, (s) => ({
      ...s,
      messages: [...s.messages, userMsg],
      title: s.title === "New conversation" ? text.slice(0, 45) : s.title,
    }))
    historyRef.current = [...historyRef.current, { role: "user", content: text }].slice(-20)
    setIsLoading(true)

    try {
      const reply = await aiService.chat({ messages: historyRef.current, systemPrompt, model: aiModel })
      historyRef.current = [...historyRef.current, { role: "assistant", content: reply }].slice(-20)
      updateSession(activeId, (s) => ({
        ...s,
        messages: [...s.messages, { id: makeId(), role: "assistant", content: reply }],
      }))
    } catch {
      updateSession(activeId, (s) => ({
        ...s,
        messages: [...s.messages, { id: makeId(), role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }],
      }))
    } finally {
      setIsLoading(false)
      inputRef.current?.focus()
    }
  }, [activeId, isLoading, updateSession, systemPrompt, aiModel])

  const handleSend = () => { const text = input.trim(); if (text) { setInput(""); sendMessage(text) } }

  const quickPrompts = useMemo(() => {
    const fallback = [
      "What career paths suit my strengths?",
      "Help me build a study plan for this week",
      "How can I improve my weakest skills?",
      "Explain the difference between WAEC and UTME",
    ]
    if (!summary) return fallback
    const prompts = []
    if (summary.activeCareerPaths[0]) prompts.push(`What skills do I need for ${summary.activeCareerPaths[0]}?`)
    if (summary.careerInterests[0]) prompts.push(`What does a career in ${summary.careerInterests[0]} look like?`)
    if (summary.goalsProgress.active > 0) prompts.push("Help me plan how to reach my current goals")
    if (summary.weakTraits[0]) prompts.push(`How can I improve my ${summary.weakTraits[0].name}?`)
    return prompts.length >= 4 ? prompts.slice(0, 4) : [...prompts, ...fallback].slice(0, 4)
  }, [summary])

  const handleNewChat = () => {
    const s = newSession(welcomeMsg)
    setSessions((prev) => { const u = [s, ...prev]; saveSessions(u); return u })
    setActiveId(s.id)
    historyRef.current = [{ role: "assistant", content: welcomeMsg }]
  }

  const handleDelete = (id) => {
    setSessions((prev) => {
      const u = prev.filter((s) => s.id !== id)
      saveSessions(u)
      if (id === activeId && u.length) setActiveId(u[0].id)
      return u
    })
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />

        {/* Page content — offset by nav sidebar, then split into history + chat */}
        <div className="flex flex-1 lg:ml-64 min-h-screen overflow-hidden transition-all duration-300">

          <HistoryPanel
            sessions={sessions}
            activeId={activeId}
            onSelect={setActiveId}
            onDelete={handleDelete}
            onNew={handleNewChat}
            collapsed={historyCollapsed}
            onToggle={() => setHistoryCollapsed((v) => !v)}
          />

          {/* Chat area */}
          <div className="flex-1 flex flex-col min-w-0 h-screen">
            {/* Header */}
            <header className="h-14 bg-white border-b border-slate-100 flex items-center justify-between px-5 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 leading-none">AI Companion</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                    {aiModel === "haiku"
                      ? <><Zap className="w-3 h-3 text-amber-500" />Claude Haiku · Fast</>
                      : <><Brain className="w-3 h-3 text-indigo-500" />Claude Sonnet · Smart</>
                    }
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-100 px-2 py-0.5 rounded-full uppercase tracking-wide">Beta</span>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
              {messages.map((m) => <Bubble key={m.id} msg={m} />)}

              {/* Quick prompts — shown only on fresh / single-message chats */}
              {messages.length === 1 && !isLoading && (
                <div className="pt-2 space-y-2">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Suggested questions</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {quickPrompts.map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => sendMessage(prompt)}
                        className="text-left text-xs bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-600 hover:text-indigo-700 px-3.5 py-2.5 rounded-xl transition-all font-medium shadow-sm leading-snug"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isLoading && <TypingDots />}
              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="shrink-0 bg-white border-t border-slate-100 px-5 py-4">
              <div className="flex items-end gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value)
                    e.target.style.height = "auto"
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend() }
                  }}
                  placeholder="Ask me anything…"
                  disabled={isLoading}
                  className="flex-1 bg-transparent resize-none text-sm text-slate-900 placeholder-slate-400 focus:outline-none leading-relaxed disabled:opacity-50 min-h-[24px]"
                  style={{ height: 24 }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="w-8 h-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center transition-all active:scale-95 shrink-0"
                >
                  <Send className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
              <p className="text-center text-[10px] text-slate-400 mt-2">AI can make mistakes · verify important information</p>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  )
}
